import type { Dispatch, SetStateAction } from 'react';

import { useErrorMessage } from '@repo/api/error-message/useErrorMessage';
import { cn } from '@repo/lib/cn';
import { fsErrorMessages } from '@repo/lib/messages/fsErrorMessages';
import { AlertCircleIcon, CircleXIcon, FileTextIcon, FolderIcon, RotateCcwIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { DirectoryProgress, FileUploadActions, FileUploadState } from '@/hooks/file-upload';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import {
  FileUploadDrawer,
  FileUploadList,
  FileUploadListAction,
  FileUploadListActions,
  FileUploadListContent,
  FileUploadListDescription,
  FileUploadListDescriptionSeparator,
  FileUploadListDescriptionText,
  FileUploadListHeader,
  FileUploadListIcon,
  FileUploadListInfo,
  FileUploadListItem,
  FileUploadListName,
  FileUploadListProgress,
  FileUploadListSize,
} from '@/components/ui/file-upload';

interface UploadFSEntiresProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  fileUploadState: FileUploadState;
  fileUploadActions: FileUploadActions;
}

export default function UploadFSEntires(uploadFSEntiresProps: UploadFSEntiresProps) {
  const { open, onOpenChange, fileUploadState, fileUploadActions } = uploadFSEntiresProps;
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const [selectedErrors, setSelectedErrors] = useState<Array<{ path: string; error: Error | null }>>([]);
  const [selectedErrorName, setSelectedErrorName] = useState<string>('');

  const totalItems = fileUploadState.files.length + fileUploadState.directories.length;

  // Auto-open drawer when entries exist
  useEffect(() => {
    if (totalItems > 0) {
      onOpenChange(true);
    } else if (totalItems === 0 && open) {
      onOpenChange(false);
    }
  }, [totalItems, onOpenChange]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      fileUploadActions.clearFiles();
    }
    onOpenChange(open);
  };

  const handleShowFileError = (fileName: string, error: Error | undefined) => {
    setSelectedErrorName(fileName);
    setSelectedErrors([{ path: fileName, error: error || null }]);
    setErrorDialogOpen(true);
  };

  const handleShowDirectoryErrors = (directoryName: string, directory: DirectoryProgress) => {
    const errors: Array<{ path: string; error: Error | null }> = [];

    const collectErrors = (dir: DirectoryProgress, parentPath: string = '') => {
      const currentPath = parentPath ? `${parentPath}/${dir.name}` : dir.name;

      dir.files.forEach((file) => {
        if (file.error) {
          errors.push({
            path: `${currentPath}/${file.file.name}`,
            error: file.error,
          });
        }
      });

      dir.subdirectories.forEach((subdir) => {
        collectErrors(subdir, currentPath);
      });

      if (dir.error) {
        errors.push({
          path: currentPath,
          error: dir.error,
        });
      }
    };

    collectErrors(directory);
    setSelectedErrorName(directoryName);
    setSelectedErrors(errors);
    setErrorDialogOpen(true);
  };

  return (
    <>
      <FileUploadDrawer open={open} onOpenChange={handleOpenChange}>
        <FileUploadList>
          {/* Render standalone files */}
          {fileUploadState.files.map((file) => (
            <FileUploadListItem key={file.id}>
              <FileUploadListHeader>
                <FileUploadListIcon>
                  <FileTextIcon />
                </FileUploadListIcon>
                <FileUploadListInfo>
                  <FileUploadListName className={cn(file.status === 'error' && 'text-text-destructive')}>{file.file.name}</FileUploadListName>
                  <FileUploadListDescription>
                    <FileUploadListSize>{file.file.size}</FileUploadListSize>
                    <FileUploadListDescriptionSeparator />
                    <FileUploadListDescriptionText>{file.progress}%</FileUploadListDescriptionText>
                  </FileUploadListDescription>
                </FileUploadListInfo>
                <FileUploadListActions>
                  {/* Error details button */}
                  {file.status === 'error' && file.error && (
                    <FileUploadListAction onClick={() => handleShowFileError(file.file.name, file.error)}>
                      <AlertCircleIcon className="text-text-destructive" />
                      <span className="sr-only">Error Details</span>
                    </FileUploadListAction>
                  )}

                  {/* Delete button */}
                  {(file.status === 'aborted' || file.status === 'error' || file.status === 'success') && (
                    <FileUploadListAction onClick={() => fileUploadActions.removeFile(file.id)} className="hover:text-text-destructive">
                      <Trash2Icon />
                      <span className="sr-only">Delete</span>
                    </FileUploadListAction>
                  )}

                  {/* Retry button */}
                  {(file.status === 'aborted' || file.status === 'error') && (
                    <FileUploadListAction onClick={() => fileUploadActions.retryUpload(file.id)}>
                      <RotateCcwIcon />
                      <span className="sr-only">Retry</span>
                    </FileUploadListAction>
                  )}

                  {/* Abort button */}
                  {file.status === 'uploading' && (
                    <FileUploadListAction onClick={() => fileUploadActions.abortFileUpload(file.id)}>
                      <XIcon />
                      <span className="sr-only">Abort</span>
                    </FileUploadListAction>
                  )}
                </FileUploadListActions>
              </FileUploadListHeader>
              <FileUploadListContent>
                <FileUploadListProgress value={file.progress} />
              </FileUploadListContent>
            </FileUploadListItem>
          ))}

          {/* Render directories */}
          {fileUploadState.directories.map((directory) => {
            const totalFiles = directory.filesCount;
            const uploadedFiles = directory.uploadedFiles;
            const progressPercent = totalFiles > 0 ? Math.round((uploadedFiles / totalFiles) * 100) : 0;
            const hasError = directory.status === 'error';

            // Check if any files are currently uploading
            const hasUploadingFiles = (dir: DirectoryProgress): boolean => {
              const hasFilesUploading = dir.files.some((file) => file.status === 'uploading');
              const hasSubdirsUploading = dir.subdirectories.some((subdir) => hasUploadingFiles(subdir));
              return hasFilesUploading || hasSubdirsUploading;
            };

            const hasAbortedFiles = (dir: DirectoryProgress): boolean => {
              const hasFilesAborted = dir.files.some((file) => file.status === 'aborted');
              const hasSubdirsAborted = dir.subdirectories.some((subdir) => hasAbortedFiles(subdir));
              return hasFilesAborted || hasSubdirsAborted;
            };

            const hasErrorFiles = (dir: DirectoryProgress): boolean => {
              const hasFilesError = dir.files.some((file) => file.status === 'error');
              const hasSubdirsError = dir.subdirectories.some((subdir) => hasErrorFiles(subdir));
              return hasFilesError || hasSubdirsError;
            };

            const isUploading = directory.status === 'uploading' || directory.status === 'creating' || hasUploadingFiles(directory);

            const isCanceled = directory.status === 'canceled' || hasAbortedFiles(directory);

            const hasAnyError = hasError || hasErrorFiles(directory);

            const showRetry = !isUploading && directory.status !== 'success' && (isCanceled || hasAnyError);

            const calculateTotalSize = (dir: DirectoryProgress): number => {
              let total = 0;
              dir.files.forEach((file) => {
                total += file.file.size;
              });
              dir.subdirectories.forEach((subdir) => {
                total += calculateTotalSize(subdir);
              });
              return total;
            };

            const totalSize = calculateTotalSize(directory);

            return (
              <FileUploadListItem key={directory.id}>
                <FileUploadListHeader>
                  <FileUploadListIcon>
                    <FolderIcon />
                  </FileUploadListIcon>
                  <FileUploadListInfo>
                    <FileUploadListName className={cn(hasError && 'text-text-destructive')}>{directory.name}</FileUploadListName>
                    <FileUploadListDescription>
                      <FileUploadListSize>{totalSize}</FileUploadListSize>
                      <FileUploadListDescriptionSeparator />
                      <FileUploadListDescriptionText>
                        {uploadedFiles}/{totalFiles} plików
                      </FileUploadListDescriptionText>
                      <FileUploadListDescriptionSeparator />
                      <FileUploadListDescriptionText>{progressPercent}%</FileUploadListDescriptionText>
                    </FileUploadListDescription>
                  </FileUploadListInfo>
                  <FileUploadListActions>
                    {/* Error details button */}
                    {hasError && (
                      <FileUploadListAction onClick={() => handleShowDirectoryErrors(directory.name, directory)}>
                        <AlertCircleIcon className="text-text-destructive" />
                        <span className="sr-only">Error Details</span>
                      </FileUploadListAction>
                    )}

                    {/* Delete button - show when not actively uploading */}
                    {!isUploading && (
                      <FileUploadListAction onClick={() => fileUploadActions.removeDirectory(directory.id)} className="hover:text-text-destructive">
                        <Trash2Icon />
                        <span className="sr-only">Delete</span>
                      </FileUploadListAction>
                    )}

                    {/* Retry button */}
                    {showRetry && (
                      <FileUploadListAction onClick={() => fileUploadActions.retryDirectoryUpload(directory.id)}>
                        <RotateCcwIcon />
                        <span className="sr-only">Retry</span>
                      </FileUploadListAction>
                    )}

                    {isUploading && (
                      <FileUploadListAction onClick={() => fileUploadActions.abortDirectoryUpload(directory.id)}>
                        <XIcon />
                        <span className="sr-only">Abort</span>
                      </FileUploadListAction>
                    )}
                  </FileUploadListActions>
                </FileUploadListHeader>
                <FileUploadListContent>
                  <FileUploadListProgress value={progressPercent} />
                </FileUploadListContent>
              </FileUploadListItem>
            );
          })}
        </FileUploadList>
      </FileUploadDrawer>

      <ErrorDetailsDialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen} errorName={selectedErrorName} errors={selectedErrors} />
    </>
  );
}

interface ErrorDetailsDialogProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  errorName: string;
  errors: Array<{ path: string; error: Error | null }>;
}

function ErrorDetailsDialog(errorDetailsDialogProps: ErrorDetailsDialogProps) {
  const { open, onOpenChange, errorName, errors } = errorDetailsDialogProps;
  const onClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'sm:max-w-148'} onCloseAutoFocus={onClose} onEscapeKeyDown={onClose}>
        <DialogDescription className={'sr-only'}>Wystąpiły błędy podczas przesyłania plików</DialogDescription>
        <DialogBody>
          <DialogHeader className={'border-border-primary border-b'}>
            <FeaturedIcon>
              <CircleXIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <DialogTitle>Błędy przesyłania: {errorName}</DialogTitle>
              <p className={'text-text-tertiary text-xs'}>Lista plików, których przesłanie nie powiodło się.</p>
            </div>
          </DialogHeader>
          <div className={'flex flex-col p-4'}>
            <ul className="space-y-2">
              {errors.map(({ path, error }, index) => (
                <ErrorDetailsItem key={`${index}-${path}`} path={path} error={error} />
              ))}
            </ul>
          </div>
        </DialogBody>
        <DialogFooter className={'border-border-primary border-t'}>
          <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
            Wyjdź
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ErrorDetailsItemProps {
  path: string;
  error: Error | null;
}

function ErrorDetailsItem(errorDetailsItemProps: ErrorDetailsItemProps) {
  const { error, path } = errorDetailsItemProps;
  const errorMessage = useErrorMessage(error, fsErrorMessages);

  return (
    <li className="flex items-center justify-between gap-4 px-3 py-2 text-sm">
      <span className="text-text-tertiary w-1/2 truncate break-all">{path}</span>
      <p className={'text-text-destructive flex-1 text-right'}>{errorMessage.detail}</p>
    </li>
  );
}
